import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureTour } from '@/components/FeatureTour';
import { getTimeOfDay } from '@/config/motivationalImages';
import axios from 'axios';
import { format, addDays, subDays } from 'date-fns';
import { API } from './dashboardConstants';
import logger from '@/utils/logger';

export function useDashboardData() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authHeaders, token } = useAuth();
  const { showTour, completeTour } = useFeatureTour();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(null);
  const [showWhatToEat, setShowWhatToEat] = useState(false);
  const [showCoachPanel, setShowCoachPanel] = useState(false);
  const [coachInitialMessage, setCoachInitialMessage] = useState('');
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [macroGap, setMacroGap] = useState(null);
  const [aiGreeting, setAiGreeting] = useState({ greeting: '', message: '' });
  const [celebration, setCelebration] = useState(null);
  const [waterCups, setWaterCups] = useState(0);
  const [latestWeight, setLatestWeight] = useState(null);
  const [todayExercise, setTodayExercise] = useState(null);
  const [lastSleep, setLastSleep] = useState(null);
  const [inspirationQuote, setInspirationQuote] = useState(null);
  const [recoveryScore, setRecoveryScore] = useState(null);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const timeOfDay = getTimeOfDay();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const weightLoggedToday = latestWeight?.date === todayStr;

  useEffect(() => {
    if (user && !user.onboarding_complete) navigate('/onboarding', { replace: true });
  }, [user, navigate]);

  const fetchMeals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/meals?date=${dateStr}`, authHeaders());
      setMeals(res.data);
    } catch (err) { logger.error('Failed to fetch meals:', err); }
    setLoading(false);
  };

  const fetchStreak = async () => {
    try {
      const res = await axios.get(`${API}/user/streak`, authHeaders());
      setStreak(res.data);
      if (res.data.milestone) {
        const key = `streak_${res.data.milestone}`;
        if (!sessionStorage.getItem(`celebration_${key}`)) {
          setCelebration(key);
          sessionStorage.setItem(`celebration_${key}`, 'true');
        }
      }
    } catch (e) { logger.error('Failed to fetch streak:', e); }
  };

  const fetchMacroGap = async () => {
    try {
      const res = await axios.get(`${API}/ai-coach/macro-gap`, authHeaders());
      setMacroGap(res.data);
    } catch (e) { logger.error('Failed to fetch macro gap:', e); }
  };

  const fetchAiGreeting = async () => {
    try {
      const res = await axios.get(`${API}/reminders/greeting`, authHeaders());
      setAiGreeting(res.data);
    } catch { setAiGreeting({ greeting: '', message: '' }); }
  };

  const fetchWater = async () => {
    try {
      const res = await axios.get(`${API}/water/today`, authHeaders());
      setWaterCups(Math.round((res.data?.total_ml || 0) / 250));
    } catch (e) { logger.error('Failed to fetch water:', e); }
  };

  const fetchWeight = async () => {
    try {
      const res = await axios.get(`${API}/weight/latest`, authHeaders());
      setLatestWeight(res.data);
    } catch (e) { logger.error('Failed to fetch weight:', e); }
  };

  const fetchExercise = async () => {
    try {
      const res = await axios.get(`${API}/exercise?date=${dateStr}`, authHeaders());
      if (res.data?.length > 0) setTodayExercise(res.data[res.data.length - 1]);
    } catch (e) { logger.error('Failed to fetch exercise:', e); }
  };

  const fetchSleep = async () => {
    try {
      const res = await axios.get(`${API}/sleep?date=${dateStr}`, authHeaders());
      if (res.data) setLastSleep(res.data);
    } catch (e) { logger.error('Failed to fetch sleep:', e); }
  };

  const fetchInspiration = async () => {
    try {
      const res = await axios.get(`${API}/inspiration/quote`, authHeaders());
      if (res.data?.quote) setInspirationQuote(res.data);
    } catch (e) { logger.error('Failed to fetch inspiration:', e); }
  };

  const fetchRecoveryScore = async () => {
    try {
      const res = await axios.get(`${API}/recovery/history`, authHeaders());
      if (res.data?.days) {
        const today = res.data.days[res.data.days.length - 1];
        setRecoveryScore(today?.score || null);
      }
    } catch (e) { logger.error('Failed to fetch recovery:', e); }
  };

  // LAYER 1: Re-fetch on every navigation to Dashboard
  useEffect(() => {
    fetchMeals();
    if (isToday) {
      fetchStreak();
      fetchMacroGap();
      fetchAiGreeting();
      fetchWater();
      fetchWeight();
      fetchExercise();
      fetchSleep();
      fetchInspiration();
      fetchRecoveryScore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, location.key]);

  // Celebration + exercise modal from navigation state
  useEffect(() => {
    if (location.state?.celebration) {
      setCelebration(location.state.celebration);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.openExercise) {
      setShowExerciseModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // LAYER 2: Re-fetch on page focus
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchMeals();
    };
    const handleFocus = () => fetchMeals();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  // Listen for water-logged, weight-logged, and other events from modals/trackers
  useEffect(() => {
    const handleWaterLogged = () => fetchWater();
    const handleRefresh = () => {
      fetchWater();
      fetchWeight();
      fetchExercise();
      fetchSleep();
    };
    const handleMealLogged = () => fetchMeals();
    const handleWeightLogged = () => fetchWeight();
    window.addEventListener('water-logged', handleWaterLogged);
    window.addEventListener('nuri-water-logged', handleWaterLogged);
    window.addEventListener('nuri-data-refresh', handleRefresh);
    window.addEventListener('nuri-meal-logged', handleMealLogged);
    window.addEventListener('weight-logged', handleWeightLogged);
    return () => {
      window.removeEventListener('water-logged', handleWaterLogged);
      window.removeEventListener('nuri-water-logged', handleWaterLogged);
      window.removeEventListener('nuri-data-refresh', handleRefresh);
      window.removeEventListener('nuri-meal-logged', handleMealLogged);
      window.removeEventListener('weight-logged', handleWeightLogged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  const dailyTotals = useMemo(() => {
    return meals.reduce((acc, m) => {
      const t = m.final_analysis?.totals || {};
      return {
        calories: acc.calories + (t.calories || 0),
        protein: acc.protein + (t.protein || 0),
        carbs: acc.carbs + (t.carbs || 0),
        fat: acc.fat + (t.fat || 0),
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
  }, [meals]);

  const targets = {
    calories: user?.calories_target || 2000,
    protein: user?.protein_target || 150,
    carbs: user?.carbs_target || 250,
    fat: user?.fat_target || 65,
  };

  const remaining = targets.calories - dailyTotals.calories;
  const protRemaining = targets.protein - Math.round(dailyTotals.protein);

  return {
    navigate, user, authHeaders, token, selectedDate, setSelectedDate,
    meals, loading, streak, macroGap, aiGreeting,
    showWhatToEat, setShowWhatToEat,
    showCoachPanel, setShowCoachPanel,
    coachInitialMessage, setCoachInitialMessage,
    showExerciseModal, setShowExerciseModal,
    celebration, setCelebration,
    waterCups, latestWeight, todayExercise, lastSleep, setLastSleep,
    inspirationQuote, recoveryScore,
    showTour, completeTour,
    dateStr, isToday, timeOfDay, firstName, weightLoggedToday,
    dailyTotals, targets, remaining, protRemaining,
    addDays, subDays,
  };
}
